/**
 * diagnostics.js — passive page-context recorder for bug reports.
 *
 * Senior QA bug reports need more than a free-text message + URL. This
 * service runs from app boot and maintains ring buffers of:
 *
 *   • console output (log / info / warn / error)
 *   • failed + slow network requests (fetch + XHR)
 *   • route changes (history.pushState / replaceState + popstate)
 *   • unhandled errors (window.error) and promise rejections
 *
 * The BugReportButton calls `snapshotDiagnostics()` the moment the
 * modal opens and includes the payload on every report — both in the
 * Firestore write and in the "Copy diagnostic" markdown export.
 *
 * Design constraints:
 *   • Cheap: no dependencies, no allocations in the hot path beyond
 *     pushing to a bounded array (.push + .shift if over cap).
 *   • Safe: every wrapper catches around the original call so a
 *     diagnostics bug never breaks the underlying browser API.
 *   • Bounded: each buffer is capped, each entry is string-clamped,
 *     and the snapshot output is trimmed to stay well under
 *     Firestore's 1 MiB doc limit even on a chatty page.
 *
 * Install once via `installDiagnostics()` in App.jsx — idempotent, so
 * a second call is a no-op (HMR friendly in dev).
 */

const CONSOLE_CAP   = 100   // most recent N console calls
const NETWORK_CAP   = 30    // most recent N non-2xx / slow / errored requests
const ROUTE_CAP     = 20    // most recent N route changes
const ERRORS_CAP    = 20    // most recent N window.error + unhandledrejection
const SLOW_REQ_MS   = 4000  // requests slower than this also get recorded
const STR_CLAMP     = 600   // per-entry string length cap

const consoleBuf = []
const networkBuf = []
const routeBuf   = []
const errorsBuf  = []

let installed = false

/** Bounded push — shifts oldest when at cap so the buffer reads chronologically. */
function pushBounded(buf, entry, cap) {
  buf.push(entry)
  if (buf.length > cap) buf.shift()
}

/** Stringify a console argument without throwing — circular refs, DOM nodes, etc. */
function safeStringify(arg) {
  try {
    if (arg === null || arg === undefined) return String(arg)
    if (typeof arg === 'string') return arg
    if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg)
    if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`
    if (typeof arg === 'function') return `[function ${arg.name || 'anonymous'}]`
    if (arg && arg.nodeType) return `[DOMNode <${arg.nodeName?.toLowerCase?.() || '?'}>]`
    return JSON.stringify(arg)
  } catch {
    return '[unstringifiable]'
  }
}

function clamp(s, n = STR_CLAMP) {
  if (typeof s !== 'string') return s
  return s.length > n ? s.slice(0, n) + `…(+${s.length - n} chars)` : s
}

/** Install console wrappers — calls the original after recording. */
function wrapConsole() {
  const original = {
    log:   console.log,
    info:  console.info,
    warn:  console.warn,
    error: console.error,
  }
  for (const level of ['log', 'info', 'warn', 'error']) {
    console[level] = (...args) => {
      try {
        pushBounded(consoleBuf, {
          t:     Date.now(),
          level,
          msg:   clamp(args.map(safeStringify).join(' ')),
        }, CONSOLE_CAP)
      } catch { /* never break console */ }
      return original[level].apply(console, args)
    }
  }
}

/** Install fetch + XHR wrappers — captures only failed / slow / errored requests. */
function wrapNetwork() {
  if (typeof window === 'undefined') return

  // ── fetch ──
  const origFetch = window.fetch
  if (origFetch) {
    window.fetch = async function (input, init) {
      const start = performance.now()
      const url   = (typeof input === 'string' ? input : input?.url) || ''
      const method = (init?.method || (typeof input !== 'string' && input?.method) || 'GET').toUpperCase()
      try {
        const res = await origFetch.apply(this, arguments)
        const ms  = Math.round(performance.now() - start)
        if (!res.ok || ms > SLOW_REQ_MS) {
          pushBounded(networkBuf, {
            t: Date.now(), kind: 'fetch',
            method, url: clamp(url, 200),
            status: res.status,
            ms,
            ok: res.ok,
          }, NETWORK_CAP)
        }
        return res
      } catch (err) {
        const ms = Math.round(performance.now() - start)
        pushBounded(networkBuf, {
          t: Date.now(), kind: 'fetch',
          method, url: clamp(url, 200),
          status: 0,
          ms,
          ok: false,
          error: clamp(String(err?.message || err), 200),
        }, NETWORK_CAP)
        throw err
      }
    }
  }

  // ── XHR ──
  const OrigXHR = window.XMLHttpRequest
  if (OrigXHR) {
    const origOpen = OrigXHR.prototype.open
    const origSend = OrigXHR.prototype.send
    OrigXHR.prototype.open = function (method, url) {
      this.__diag_method = String(method || 'GET').toUpperCase()
      this.__diag_url    = String(url || '')
      this.__diag_start  = performance.now()
      return origOpen.apply(this, arguments)
    }
    OrigXHR.prototype.send = function () {
      const xhr = this
      const finalize = () => {
        try {
          const ms = Math.round(performance.now() - (xhr.__diag_start || performance.now()))
          const status = xhr.status || 0
          if (status === 0 || status >= 400 || ms > SLOW_REQ_MS) {
            pushBounded(networkBuf, {
              t: Date.now(), kind: 'xhr',
              method: xhr.__diag_method || 'GET',
              url:    clamp(xhr.__diag_url || '', 200),
              status,
              ms,
              ok:     status >= 200 && status < 300,
            }, NETWORK_CAP)
          }
        } catch { /* swallow */ }
      }
      xhr.addEventListener('loadend', finalize)
      return origSend.apply(this, arguments)
    }
  }
}

/** Install route-change listeners — covers pushState, replaceState, popstate. */
function wrapHistory() {
  if (typeof window === 'undefined' || !window.history) return
  const record = (kind) => {
    pushBounded(routeBuf, {
      t:    Date.now(),
      kind,
      path: clamp(window.location?.pathname || '', 200),
      search: clamp(window.location?.search || '', 200),
      hash:   clamp(window.location?.hash || '', 100),
    }, ROUTE_CAP)
  }
  const origPush    = window.history.pushState
  const origReplace = window.history.replaceState
  window.history.pushState = function () {
    const r = origPush.apply(this, arguments)
    record('push')
    return r
  }
  window.history.replaceState = function () {
    const r = origReplace.apply(this, arguments)
    record('replace')
    return r
  }
  window.addEventListener('popstate', () => record('popstate'))
  // Seed the buffer with the initial route so a report from a page
  // the user landed on directly still shows where they started.
  record('initial')
}

/** Install error + promise-rejection listeners. */
function wrapErrors() {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (e) => {
    pushBounded(errorsBuf, {
      t:       Date.now(),
      kind:    'error',
      message: clamp(String(e?.message || ''), 300),
      source:  clamp(String(e?.filename || ''), 200),
      line:    e?.lineno || null,
      col:     e?.colno || null,
      stack:   clamp(String(e?.error?.stack || ''), 800),
    }, ERRORS_CAP)
  })
  window.addEventListener('unhandledrejection', (e) => {
    pushBounded(errorsBuf, {
      t:       Date.now(),
      kind:    'unhandledrejection',
      message: clamp(String(e?.reason?.message || e?.reason || ''), 300),
      stack:   clamp(String(e?.reason?.stack || ''), 800),
    }, ERRORS_CAP)
  })
}

/**
 * Install all wrappers + listeners. Call once at app boot. Idempotent —
 * a second call is a no-op so HMR doesn't double-record.
 */
export function installDiagnostics() {
  if (installed) return
  installed = true
  wrapConsole()
  wrapNetwork()
  wrapHistory()
  wrapErrors()
}

// ─── Environment fingerprint ──────────────────────────────────────────────

/** Parsed browser + OS bucketing from userAgent. Best-effort; falls back to raw UA. */
function parseUserAgent(ua) {
  const u = (ua || '').toLowerCase()
  let browser = 'Unknown'
  let browserVer = ''
  if (u.includes('edg/'))          { browser = 'Edge';    browserVer = u.match(/edg\/([\d.]+)/)?.[1] || '' }
  else if (u.includes('chrome/'))  { browser = 'Chrome';  browserVer = u.match(/chrome\/([\d.]+)/)?.[1] || '' }
  else if (u.includes('firefox/')) { browser = 'Firefox'; browserVer = u.match(/firefox\/([\d.]+)/)?.[1] || '' }
  else if (u.includes('safari/'))  { browser = 'Safari';  browserVer = u.match(/version\/([\d.]+)/)?.[1] || '' }

  let os = 'Unknown'
  if      (u.includes('iphone') || u.includes('ipad') || u.includes('ipod')) os = 'iOS'
  else if (u.includes('android')) os = 'Android'
  else if (u.includes('mac os')) os = 'macOS'
  else if (u.includes('windows')) os = 'Windows'
  else if (u.includes('linux'))   os = 'Linux'

  const isMobile = /mobile|iphone|ipad|ipod|android/i.test(ua || '')
  return { browser, browserVer, os, isMobile }
}

/**
 * Build the complete diagnostic snapshot. Pure function — does not
 * mutate the buffers. Safe to call repeatedly; each call produces a
 * fresh capture of the current page state.
 *
 * @param {object} extra — caller-supplied context to merge in (user,
 *                         theme mode, viewMode, accountType, activeOrgId).
 * @returns {object} payload safe to write to Firestore + safe to
 *                   render as a markdown block.
 */
// VIN-2026-44 (2026-05-26): redact sensitive query params from URLs
// captured into the diagnostic blob. Magic-link sign-in
// (`?apiKey=...&oobCode=...`), ticket-claim transfers
// (`?token=...&transferToken=...`), Stripe checkout returns, and
// password-reset action codes all carry single-use credentials in the
// URL. Without redaction, a tester who hits the bug-report button on
// one of those pages would write the credential into Firestore +
// clipboard. Self-PII for internal testers, but a hard problem for
// any wider rollout — easier to redact unconditionally than to gate
// the diagnostic surface by route.
const SENSITIVE_QUERY_KEYS = new Set([
  'oobcode',          // Firebase magic-link / password-reset action code
  'apikey',           // Firebase web API key in some flows
  'token',            // generic auth/share token (TicketClaim, sharedWines)
  'transfertoken',    // ticket-transfer claim token
  '_at',              // access-token-ish naming
  'access_token',     // OAuth literals
  'id_token',
  'refresh_token',
  'code',             // OAuth auth code
  'state',            // OAuth state
])

function redactUrlString(input) {
  if (!input) return input
  try {
    const u = new URL(input, 'https://placeholder.local')
    let touched = false
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        u.searchParams.set(key, '<redacted>')
        touched = true
      }
    }
    if (!touched) return input
    // Preserve relative vs. absolute. If the input was already relative
    // (started without a scheme), strip the placeholder origin back out.
    if (/^https?:\/\//i.test(input) || /^[a-z]+:\/\//i.test(input)) {
      return u.toString()
    }
    return u.pathname + u.search + u.hash
  } catch {
    // Not a parseable URL — return as-is. The capture is best-effort.
    return input
  }
}

function redactSearchString(input) {
  if (!input) return input
  // input is the raw `?foo=bar&baz=qux` string. Treat it like the search
  // half of a URL and route through redactUrlString.
  try {
    const u = new URL('https://placeholder.local/' + input)
    let touched = false
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        u.searchParams.set(key, '<redacted>')
        touched = true
      }
    }
    return touched ? ('?' + u.searchParams.toString()) : input
  } catch {
    return input
  }
}

export function snapshotDiagnostics(extra = {}) {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const { browser, browserVer, os, isMobile } = parseUserAgent(ua)

  const env = {
    // VIN-2026-44: redact sensitive query params before capture. See
    // SENSITIVE_QUERY_KEYS above for the canonical list.
    href:     typeof window !== 'undefined' ? redactUrlString(window.location?.href || '') : '',
    pathname: typeof window !== 'undefined' ? (window.location?.pathname || '') : '',
    search:   typeof window !== 'undefined' ? redactSearchString(window.location?.search || '') : '',
    hash:     typeof window !== 'undefined' ? (window.location?.hash || '') : '',
    referrer: typeof document !== 'undefined' ? redactUrlString(document.referrer || '') : '',
    userAgent: clamp(ua, 400),
    browser, browserVer, os, isMobile,
    viewportW: typeof window !== 'undefined' ? window.innerWidth : null,
    viewportH: typeof window !== 'undefined' ? window.innerHeight : null,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
    online: typeof navigator !== 'undefined' ? !!navigator.onLine : null,
    locale: typeof navigator !== 'undefined' ? (navigator.language || '') : '',
    timezone: (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { return '' }
    })(),
    timestamp: new Date().toISOString(),
    buildSha:  (typeof __VINARAI_BUILD_SHA__ !== 'undefined' ? __VINARAI_BUILD_SHA__ : 'dev'),
    buildTime: (typeof __VINARAI_BUILD_TIME__ !== 'undefined' ? __VINARAI_BUILD_TIME__ : ''),
  }

  return {
    env,
    user: {
      uid:         extra.uid || null,
      email:       extra.email || null,
      displayName: extra.displayName || null,
      tier:        extra.tier || null,
      accountType: extra.accountType || null,
      viewMode:    extra.viewMode || null,
      activeOrgId: extra.activeOrgId || null,
      themeMode:   extra.themeMode || null,
    },
    console: consoleBuf.slice(),
    network: networkBuf.slice(),
    routes:  routeBuf.slice(),
    errors:  errorsBuf.slice(),
  }
}

// ─── Markdown export ──────────────────────────────────────────────────────

/** Format a Date / epoch ms as HH:MM:SS.mmm — short enough to read inline. */
function fmtTime(t) {
  try {
    const d = typeof t === 'number' ? new Date(t) : new Date()
    return d.toISOString().slice(11, 23)
  } catch {
    return ''
  }
}

/**
 * Turn a diagnostics snapshot into a markdown block suitable for
 * pasting into an issue tracker or chat. Sections are collapsible
 * via <details> so the block reads compactly until expanded.
 */
export function toMarkdown(snapshot, { message = '', bugType = '', steps = '' } = {}) {
  const { env, user, console: cons, network, routes, errors } = snapshot

  const userBlock = [
    `- **uid:** \`${user.uid || '(anonymous)'}\``,
    `- **email:** ${user.email || '(unknown)'}`,
    `- **name:** ${user.displayName || '(unknown)'}`,
    `- **tier:** ${user.tier || '(unknown)'}`,
    `- **accountType:** ${user.accountType || '(unknown)'}`,
    `- **viewMode:** ${user.viewMode || '(unknown)'}`,
    `- **activeOrgId:** ${user.activeOrgId || '(none)'}`,
    `- **themeMode:** ${user.themeMode || '(unknown)'}`,
  ].join('\n')

  const envBlock = [
    `- **URL:** ${env.href}`,
    `- **Build:** ${env.buildSha}${env.buildTime ? ` (${env.buildTime})` : ''}`,
    `- **Browser:** ${env.browser} ${env.browserVer} on ${env.os}${env.isMobile ? ' (mobile)' : ''}`,
    `- **Viewport:** ${env.viewportW}×${env.viewportH} @ ${env.devicePixelRatio}x`,
    `- **Locale / TZ:** ${env.locale} / ${env.timezone}`,
    `- **Online:** ${env.online === null ? 'unknown' : env.online}`,
    `- **Referrer:** ${env.referrer || '(none)'}`,
    `- **Timestamp:** ${env.timestamp}`,
    `- **UA:** \`${env.userAgent}\``,
  ].join('\n')

  const fmtConsole = (e) => `[${fmtTime(e.t)}] ${e.level.toUpperCase().padEnd(5)} ${e.msg}`
  const fmtNetwork = (e) => `[${fmtTime(e.t)}] ${e.method} ${e.url} → ${e.status || 'err'} (${e.ms}ms)${e.error ? ' — ' + e.error : ''}`
  const fmtRoute   = (e) => `[${fmtTime(e.t)}] ${e.kind.padEnd(8)} ${e.path}${e.search}${e.hash}`
  const fmtError   = (e) => `[${fmtTime(e.t)}] ${e.kind}: ${e.message}${e.source ? ` (${e.source}:${e.line}:${e.col})` : ''}${e.stack ? '\n' + e.stack : ''}`

  const section = (title, items, fmt) => {
    if (!items?.length) return `<details><summary>${title} (0)</summary>\n\n_None._\n</details>`
    return [
      `<details><summary>${title} (${items.length})</summary>`,
      '',
      '```',
      ...items.map(fmt),
      '```',
      '</details>',
    ].join('\n')
  }

  return [
    '## Vinarai bug report',
    '',
    bugType ? `**Type:** ${bugType}` : '',
    message ? `\n**What happened:**\n${message}` : '',
    steps ? `\n**Steps to reproduce:**\n${steps}` : '',
    '',
    '### User',
    userBlock,
    '',
    '### Environment',
    envBlock,
    '',
    '### Recent console',
    section('Console history', cons, fmtConsole),
    '',
    '### Recent network failures / slow requests',
    section('Network', network, fmtNetwork),
    '',
    '### Recent routes',
    section('Route history', routes, fmtRoute),
    '',
    '### Errors / unhandled rejections',
    section('Errors', errors, fmtError),
    '',
  ].filter(Boolean).join('\n')
}

// Test-only escape hatch — lets unit tests clear state between cases.
// Not used by app code.
export function _resetDiagnostics() {
  consoleBuf.length = 0
  networkBuf.length = 0
  routeBuf.length   = 0
  errorsBuf.length  = 0
  installed = false
}
